import React from 'react';
import SEO from '../../../components/SEO';
import { CtaBlock } from '../components/MentoringPrimitives';
import { getMentoringContent, getMentoringPageContent, getMentoringSeo, getMentoringSharedContent } from '../mentoringContent';
import { getMentoringAlternates, getMentoringPath } from '../mentoringRoutes';
import { getNextSaturday } from '../nextCohort';

const SITE_ORIGIN = 'https://csl-brands.com';
const PROGRAM_NAME = 'CSL BRANDS Mentoring Program';

const findFact = (facts, ...needles) => facts.find((fact) => (
  needles.some((needle) => fact.label?.toLowerCase().includes(needle.toLowerCase()))
))?.value;

/**
 * Builds the page's JSON-LD: the programme as an educational offering with its
 * schedule, plus a FAQ block on the program page so answer engines can quote
 * the Q&A directly.
 */
const buildStructuredData = (locale, pageKey) => {
  const bundle = getMentoringContent(locale);
  const facts = bundle?.[pageKey]?.hero?.facts ?? [];
  const duration = findFact(facts, 'durée', 'duration');
  const start = findFact(facts, 'début', 'start');
  const seats = findFact(facts, 'places', 'seats');
  const description = getMentoringSeo(locale, pageKey)?.description;
  const cohortDate = getNextSaturday();
  const startDate = [cohortDate.getFullYear(), String(cohortDate.getMonth() + 1).padStart(2, '0'), String(cohortDate.getDate()).padStart(2, '0')].join('-');

  const nodes = [
    {
      '@type': 'EducationalOrganization',
      '@id': `${SITE_ORIGIN}/#mentoring-program`,
      name: PROGRAM_NAME,
      url: `${SITE_ORIGIN}${getMentoringPath(locale, 'index')}`,
      parentOrganization: { '@id': `${SITE_ORIGIN}/#organization` },
      description,
    },
    {
      '@type': 'Course',
      name: PROGRAM_NAME,
      description,
      url: `${SITE_ORIGIN}${getMentoringPath(locale, pageKey)}`,
      provider: { '@id': `${SITE_ORIGIN}/#mentoring-program` },
      inLanguage: locale,
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'onsite',
        name: `${PROGRAM_NAME} — ${start ?? ''}`.trim(),
        startDate,
        ...(duration ? { courseWorkload: 'P6M' } : {}),
        ...(seats ? { maximumEnrollment: Number.parseInt(seats, 10) || undefined } : {}),
        location: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Douala',
            addressCountry: 'CM',
          },
        },
      },
    },
  ];

  const faqItems = pageKey === 'program' ? bundle?.program?.faq?.items ?? [] : [];

  if (faqItems.length > 0) {
    nodes.push({
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    });
  }

  return nodes;
};

/**
 * Wraps a mentoring page with its SEO head and the closing CTA that closes
 * every page in the section.
 */
export const MentoringPageShell = ({ locale, pageKey, children }) => {
  const content = getMentoringPageContent(locale, pageKey);
  const seo = getMentoringSeo(locale, pageKey);
  const path = getMentoringPath(locale, pageKey);

  if (!content || !seo || !path) {
    return null;
  }

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        path={path}
        alternates={getMentoringAlternates(pageKey)}
        structuredData={buildStructuredData(locale, pageKey)}
      />
      {children}
      {getMentoringSharedContent(locale)?.cta && <CtaBlock {...getMentoringSharedContent(locale).cta} />}
    </>
  );
};
